import React, { useState, useEffect } from "react";
import {
    Button,
    Modal,
    Form,
    Input,
    Select,
    InputNumber,
    message,
    Space,
    Spin,
    Typography,
    Card,
    Row,
    Col,
    Table,
    Tag,
    Divider,
    Alert,
} from "antd";
import {
    RocketOutlined,
    PlusOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    DollarOutlined,
} from "@ant-design/icons";
import { useScheduleEditor } from "../context/ScheduleEditorContext";
import enhancedApi from "../../../../api/enhancedApi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

const { Text, Title } = Typography;
const { Option } = Select;

dayjs.extend(utc);

/**
 * 航班智能规划组件
 * 基于DFS+剪枝算法，实现全局最优的多轮往返规划
 */
const FlightPlannerButton = () => {
    const { state } = useScheduleEditor();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isPlanning, setIsPlanning] = useState(false);
    const [planningResult, setPlanningResult] = useState(null);
    const [form] = Form.useForm();
    const [salesRoutes, setSalesRoutes] = useState([]);
    const [availableAirports, setAvailableAirports] = useState([]);
    const [availableLegs, setAvailableLegs] = useState([]);

    const { schedule, importFunction } = state;

    // 加载可用机场和航段
    useEffect(() => {
        const loadData = async () => {
            try {
                const [airportsRes, legsRes] = await Promise.all([
                    enhancedApi.flightPlanner.getAvailableAirports(),
                    enhancedApi.flightPlanner.getAvailableLegs(),
                ]);
                setAvailableAirports(airportsRes.data.airports || []);
                setAvailableLegs(legsRes.data.legs || []);
            } catch (error) {
                console.error("加载数据失败:", error);
            }
        };
        if (isModalVisible) {
            loadData();
        }
    }, [isModalVisible]);

    // 预定义的销售航线模板（只定义去程，回程由算法自动规划）
    const routeTemplates = [
        {
            route_id: "HGH-BUD",
            origin: "HGH",
            destination: "BUD",
            value: 80000,
            outbound: ["HGH", "NQZ", "BUD"],
        },
        {
            route_id: "HKG-LGG",
            origin: "HKG",
            destination: "LGG",
            value: 100000,
            outbound: ["HKG", "NQZ", "LGG"],
        },
        {
            route_id: "HGH-LGG",
            origin: "HGH",
            destination: "LGG",
            value: 90000,
            outbound: ["HGH", "NQZ", "LGG"],
        },
    ];

    // 默认过站成本配置（每小时美元）
    const defaultTurnaroundCosts = {
        HGH: 500,
        HKG: 500,
        NQZ: 500,
        LGG: 200,
        BUD: 200,
        HSA: 500,
        DWC: 500,
    };

    const [turnaroundCosts, setTurnaroundCosts] = useState(defaultTurnaroundCosts);

    // 打开规划对话框
    const handlePlanClick = () => {
        if (!schedule?.start_date || !schedule?.end_date) {
            message.warning("请先设置计划的开始和结束日期");
            return;
        }

        setIsModalVisible(true);
        setPlanningResult(null);
        setSalesRoutes([]);

        form.setFieldsValue({
            aircraft_reg: schedule?.aircraft_reg || "9H-FLM",
            initial_location: "HGH",
        });
    };

    // 添加销售航线
    const handleAddRoute = (template) => {
        const exists = salesRoutes.some((r) => r.route_id === template.route_id);
        if (exists) {
            message.warning("该航线已添加");
            return;
        }
        setSalesRoutes([...salesRoutes, { ...template }]);
    };

    // 删除销售航线
    const handleRemoveRoute = (routeId) => {
        setSalesRoutes(salesRoutes.filter((r) => r.route_id !== routeId));
    };

    // 更新航线价值
    const handleValueChange = (routeId, value) => {
        setSalesRoutes(salesRoutes.map((r) => (r.route_id === routeId ? { ...r, value } : r)));
    };

    // 更新过站成本
    const handleTurnaroundCostChange = (airportCode, cost) => {
        setTurnaroundCosts({
            ...turnaroundCosts,
            [airportCode]: cost,
        });
    };

    // 执行规划
    const handlePlanSubmit = async () => {
        if (salesRoutes.length === 0) {
            message.warning("请至少添加一条销售航线");
            return;
        }

        try {
            const values = await form.validateFields();
            setIsPlanning(true);
            setPlanningResult(null);

            // 过站成本直接传递
            const turnaroundCostsMap = { ...turnaroundCosts };

            // 构建请求
            const request = {
                start_date: schedule.start_date,
                end_date: schedule.end_date,
                aircraft_reg: values.aircraft_reg,
                initial_location: values.initial_location,
                sales_routes: salesRoutes.map((r) => ({
                    route_id: r.route_id,
                    origin: r.origin,
                    destination: r.destination,
                    value: r.value,
                })),
                route_chains: salesRoutes.map((r, idx) => ({
                    chain_id: `${r.route_id}-chain-${idx}`,
                    route_id: r.route_id,
                    outbound_chain: r.outbound,
                })),
                turnaround_costs: turnaroundCostsMap,
                max_search_time: 60,
            };

            console.log("规划请求:", request);

            const response = await enhancedApi.flightPlanner.planFlights(request);

            console.log("规划响应:", response);

            if (response.data.success) {
                setPlanningResult(response.data);
                message.success(response.data.message);
            } else {
                message.error(response.data.message || "规划失败");
                setPlanningResult(response.data);
            }
        } catch (error) {
            console.error("规划失败:", error);
            message.error("规划失败：" + (error.response?.data?.detail || error.message));
        } finally {
            setIsPlanning(false);
        }
    };

    // 应用规划结果
    const handleApplyResult = async () => {
        if (!planningResult?.activities || !importFunction) {
            message.warning("没有可应用的结果");
            return;
        }

        try {
            const importData = planningResult.activities.map((act) => ({
                flight_number: act.flight_number,
                departure_airport: act.departure_airport,
                arrival_airport: act.arrival_airport,
                std: act.departure_time,
                sta: act.arrival_time,
                remarks: `智能规划 - ${act.demand_id}`,
            }));

            await importFunction(importData);
            message.success(`成功导入 ${importData.length} 个航班`);
            setIsModalVisible(false);
        } catch (error) {
            message.error("导入失败：" + error.message);
        }
    };

    // 销售航线表格列
    const routeColumns = [
        {
            title: "航线",
            dataIndex: "route_id",
            key: "route_id",
            render: (_, record) => (
                <Space>
                    <Tag color="blue">{record.origin}</Tag>
                    <span>→</span>
                    <Tag color="green">{record.destination}</Tag>
                </Space>
            ),
        },
        {
            title: "去程",
            dataIndex: "outbound",
            key: "outbound",
            render: (chain) => chain.join(" → "),
        },
        {
            title: "收益 (美元)",
            dataIndex: "value",
            key: "value",
            render: (value, record) => (
                <InputNumber
                    value={value}
                    min={1000}
                    step={10000}
                    formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(v) => v.replace(/\$\s?|(,*)/g, "")}
                    onChange={(v) => handleValueChange(record.route_id, v)}
                    style={{ width: 130 }}
                />
            ),
        },
        {
            title: "操作",
            key: "action",
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveRoute(record.route_id)}
                />
            ),
        },
    ];

    // 渲染规划结果
    const renderResult = () => {
        if (!planningResult) return null;

        const { success, message: msg, activities, executed_rounds, total_value, total_cost, net_profit, execution_time } =
            planningResult;

        return (
            <Card
                title={
                    <Space>
                        <CheckCircleOutlined style={{ color: success ? "#52c41a" : "#ff4d4f" }} />
                        规划结果
                    </Space>
                }
                style={{ marginTop: 16 }}
            >
                <Alert
                    type={success ? "success" : "error"}
                    message={msg}
                    style={{ marginBottom: 16 }}
                />

                {success && (
                    <>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Card size="small" style={{ textAlign: "center" }}>
                                    <Text type="secondary">往返轮数</Text>
                                    <Title level={3} style={{ margin: 0 }}>
                                        {executed_rounds?.length || 0}
                                    </Title>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card size="small" style={{ textAlign: "center" }}>
                                    <Text type="secondary">总航班数</Text>
                                    <Title level={3} style={{ margin: 0 }}>
                                        {activities?.length || 0}
                                    </Title>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card size="small" style={{ textAlign: "center", background: "#f6ffed" }}>
                                    <Text type="secondary">总收益</Text>
                                    <Title level={3} style={{ margin: 0, color: "#52c41a" }}>
                                        ${total_value?.toLocaleString()}
                                    </Title>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card size="small" style={{ textAlign: "center", background: "#e6f7ff" }}>
                                    <Text type="secondary">净利润</Text>
                                    <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
                                        ${net_profit?.toLocaleString()}
                                    </Title>
                                </Card>
                            </Col>
                        </Row>

                        <Divider>航班预览（前10个）</Divider>
                        <div style={{ maxHeight: 200, overflow: "auto" }}>
                            {activities?.slice(0, 10).map((act, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        padding: "8px 12px",
                                        borderBottom: "1px solid #f0f0f0",
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Space>
                                        <Text code>{act.flight_number}</Text>
                                        <Tag color={act.flight_type === "REVENUE" ? "blue" : "default"}>
                                            {act.departure_airport} → {act.arrival_airport}
                                        </Tag>
                                    </Space>
                                    <Text type="secondary">
                                        {dayjs.utc(act.departure_time).format("MM-DD HH:mm")} UTC
                                    </Text>
                                </div>
                            ))}
                            {activities?.length > 10 && (
                                <Text type="secondary" style={{ padding: 8, display: "block" }}>
                                    ... 还有 {activities.length - 10} 个航班
                                </Text>
                            )}
                        </div>

                        <div style={{ marginTop: 16, textAlign: "right" }}>
                            <Text type="secondary">计算耗时: {execution_time?.toFixed(2)}秒</Text>
                        </div>
                    </>
                )}
            </Card>
        );
    };

    if (!schedule) return null;

    return (
        <>
            <Button type="primary" icon={<RocketOutlined />} onClick={handlePlanClick}>
                智能规划
            </Button>

            <Modal
                title={
                    <Space>
                        <RocketOutlined />
                        航班智能规划
                    </Space>
                }
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                width={900}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                        取消
                    </Button>,
                    <Button
                        key="plan"
                        type="primary"
                        loading={isPlanning}
                        onClick={handlePlanSubmit}
                        icon={<RocketOutlined />}
                        disabled={salesRoutes.length === 0}
                    >
                        开始规划
                    </Button>,
                    planningResult?.success && (
                        <Button
                            key="apply"
                            type="primary"
                            onClick={handleApplyResult}
                            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                        >
                            应用结果
                        </Button>
                    ),
                ]}
            >
                {/* 基本参数 */}
                <Card title="基本参数" size="small" style={{ marginBottom: 16 }}>
                    <Form form={form} layout="inline">
                        <Form.Item label="时间范围">
                            <Text>
                                {dayjs(schedule?.start_date).format("YYYY-MM-DD")} ~{" "}
                                {dayjs(schedule?.end_date).format("YYYY-MM-DD")}
                            </Text>
                        </Form.Item>
                        <Form.Item
                            label="飞机注册号"
                            name="aircraft_reg"
                            rules={[{ required: true }]}
                        >
                            <Input style={{ width: 120 }} />
                        </Form.Item>
                        <Form.Item
                            label="初始位置"
                            name="initial_location"
                            rules={[{ required: true }]}
                        >
                            <Select style={{ width: 100 }}>
                                <Option value="HGH">HGH</Option>
                                <Option value="HKG">HKG</Option>
                                <Option value="LGG">LGG</Option>
                                <Option value="BUD">BUD</Option>
                                <Option value="NQZ">NQZ</Option>
                            </Select>
                        </Form.Item>
                    </Form>
                </Card>

                {/* 销售航线配置 */}
                <Card
                    title={
                        <Space>
                            <DollarOutlined />
                            销售航线（运输需求）
                        </Space>
                    }
                    size="small"
                    extra={
                        <Select
                            placeholder="添加航线"
                            style={{ width: 200 }}
                            onChange={(value) => {
                                const template = routeTemplates.find((t) => t.route_id === value);
                                if (template) handleAddRoute(template);
                            }}
                            value={undefined}
                        >
                            {routeTemplates.map((t) => (
                                <Option
                                    key={t.route_id}
                                    value={t.route_id}
                                    disabled={salesRoutes.some((r) => r.route_id === t.route_id)}
                                >
                                    {t.route_id} (${t.value.toLocaleString()})
                                </Option>
                            ))}
                        </Select>
                    }
                >
                    {salesRoutes.length === 0 ? (
                        <Alert
                            type="info"
                            message="请从右上角下拉框添加销售航线。系统将自动规划在时间窗口内完成尽可能多的往返。"
                            showIcon
                        />
                    ) : (
                        <Table
                            dataSource={salesRoutes}
                            columns={routeColumns}
                            rowKey="route_id"
                            pagination={false}
                            size="small"
                        />
                    )}
                </Card>

                {/* 过站成本配置 */}
                <Card
                    title={
                        <Space>
                            <DollarOutlined />
                            过站成本配置（每小时美元）
                        </Space>
                    }
                    size="small"
                    style={{ marginTop: 16 }}
                >
                    <Row gutter={[16, 8]}>
                        {Object.entries(turnaroundCosts).map(([code, cost]) => (
                            <Col span={6} key={code}>
                                <Space>
                                    <Tag color="blue">{code}</Tag>
                                    <InputNumber
                                        value={cost}
                                        min={0}
                                        step={10}
                                        size="small"
                                        style={{ width: 80 }}
                                        formatter={(v) => `$${v}`}
                                        parser={(v) => v.replace("$", "")}
                                        onChange={(v) => handleTurnaroundCostChange(code, v)}
                                    />
                                </Space>
                            </Col>
                        ))}
                    </Row>
                    <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            💡 提示：过站成本越低的机场，算法越倾向于在那里等待。这会影响航班时刻的安排。
                        </Text>
                    </div>
                </Card>

                {/* 规划中状态 */}
                {isPlanning && (
                    <div style={{ textAlign: "center", marginTop: 24 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 8 }}>
                            <Text>正在使用DFS+剪枝算法搜索最优解...</Text>
                        </div>
                    </div>
                )}

                {/* 规划结果 */}
                {renderResult()}
            </Modal>
        </>
    );
};

export default FlightPlannerButton;
